import { FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { Box, IconButton, SxProps, TextField, Theme, Typography } from '@mui/material';
import type { ClientRectObject } from '@popperjs/core';
import { Controller, FieldValues, RegisterOptions, useFormContext } from 'react-hook-form';

import { CircleArrowIcon, CloseIcon, KeyPasswordIcon, LockedIcon } from 'shared/assets/icons';
import { generateValidPassword } from 'shared/utils';

import { CustomTooltip } from '../tooltip';

import styles from './input.module.scss';

interface IProps {
  type?: string;
  name: string;
  label?: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  autoComplete?: string;
  rules?: Omit<
    RegisterOptions<FieldValues, string>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  isPass?: boolean;
  isEdit?: boolean;
  onClickIcon?: () => void;
  autofill?: boolean;
  defaultValue?: string;
  suffix?: string;
  prefilled?: boolean;
  tooltip?: React.ReactNode;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  notClearable?: boolean;
  sx?: SxProps<Theme>;
}

const inputPadding = 20 as const;
const suffixGap = 10 as const;

export const Input: FC<IProps> = ({
  name,
  rules,
  label,
  placeholder,
  required,
  autoComplete,
  type,
  helperText,
  isPass,
  isEdit,
  onClickIcon,
  autofill,
  defaultValue,
  suffix,
  prefilled,
  tooltip,
  disabled,
  multiline,
  rows,
  notClearable,
  sx
}) => {
  const suffixRef = useRef<HTMLSpanElement>(null);

  const [inputRightPadding, setInputRightPadding] = useState<number>(0);

  useLayoutEffect(() => {
    const suffixWidth = suffixRef.current?.offsetWidth;
    setInputRightPadding(
      suffix && suffixWidth ? suffixWidth + (inputPadding + suffixGap) : inputPadding
    );
  }, [suffix]);

  const methods = useFormContext();
  const { control } = methods;
  const fieldError = methods?.formState.errors[name]?.message?.toString();
  const [showPassword, setShowPassword] = useState(false);
  const [toogler, setToogler] = useState(true);
  const password = useMemo(() => generateValidPassword(10), [toogler]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (prefilled) methods?.setValue(name, password);
  }, [password]);

  const resetField = () => {
    methods?.setValue(name, '');
  };
  const isRequired = !label?.includes('*') && rules?.required ? '*' : '';
  const labelColor = () => {
    if (fieldError) return 'var(--mui-palette-error-main)';
    if ((isEdit && disabled) || autofill) return 'var(--mui-palette-text-primary)';
  };

  return (
    <Box style={{ position: 'relative' }}>
      <Controller
        control={control}
        rules={rules}
        name={name}
        defaultValue={defaultValue}
        render={({ field: { onChange, onBlur, value } }) => {
          return (
            <>
              <TextField
                margin='normal'
                name={name}
                autoComplete={autoComplete}
                fullWidth
                inputRef={inputRef}
                required={required}
                slotProps={{
                  input: {
                    className: styles.input
                  },
                  inputLabel: {
                    shrink: true,
                    style: {
                      color: labelColor()
                    }
                  }
                }}
                disabled={autofill || disabled}
                id={name}
                label={label + isRequired}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                placeholder={placeholder}
                type={isPass ? (showPassword ? 'text' : 'password') : type}
                sx={{
                  m: 0,
                  p: 0,
                  fieldset: {
                    border: fieldError && '1px solid var(--mui-palette-error-main)'
                  },
                  input: {
                    padding: tooltip
                      ? '16.5px 90px 16.5px 14px'
                      : isEdit
                        ? '16.5px 40px 16.5px 14px'
                        : undefined,
                    paddingRight: suffix && inputRightPadding,
                    width: '100%',
                    outline: 'none'
                  },
                  '.MuiInputBase-root': suffix && {
                    display: 'flex',
                    width: '100%',
                    height: '56px',
                    position: 'relative',
                    fontFamily: 'sans-serif'
                  },
                  '& input.Mui-disabled': {
                    '-webkit-text-fill-color': 'var(--mui-palette-text-primary)'
                  },
                  ...sx
                }}
                multiline={multiline}
                rows={rows}
              />
              {suffix && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    display: value ? 'flex' : 'none',
                    alignItems: 'center',
                    visibility: 'hidden',
                    userSelect: 'none',
                    pointerEvents: 'none',
                    padding: '16.5px 14px'
                  }}>
                  <span style={{ overflow: 'hidden' }}>{value || placeholder}</span>
                  <span
                    style={{
                      visibility: 'visible',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    ref={suffixRef}>
                    {suffix}
                  </span>
                </div>
              )}
            </>
          );
        }}
      />
      {/* TODO Можно выделать в отдельный слайс */}
      <Box sx={{ position: 'absolute', right: 16, top: 16, display: 'flex', alignItems: 'center' }}>
        {isPass && (
          <CustomTooltip
            title={
              <Box
                display={'flex'}
                flexDirection={'row'}
                alignItems={'start'}
                sx={{ fontSize: '16px' }}>
                <KeyPasswordIcon
                  style={{
                    fontSize: '24px',
                    width: '24px',
                    height: '24px',
                    color: 'var(--mui-palette-text-light)'
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: '190px',
                    width: '190px',
                    margin: '0 6px',
                    cursor: 'pointer',
                    '& span': {
                      fontWeight: 600
                    },
                    '& p': {
                      fontFamily: 'Roboto',
                      fontSize: '15px',
                      fontStyle: 'normal',
                      fontWeight: '400',
                      lineHeight: '20px',
                      letterSpacing: '0.25px',
                      color: 'var(--mui-palette-text-light)'
                    }
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(password);
                    methods?.setValue(name, password);
                  }}>
                  <Typography variant='body1' component='span'>
                    {password}
                  </Typography>
                  <Typography variant='body1' color={'var(--mui-palette-text-light)'} component='p'>
                    Paste and save the generated password
                  </Typography>
                </Box>
                <Box
                  component={'span'}
                  sx={{ padding: '4px', marginTop: '-4px', cursor: 'pointer' }}
                  onClick={() => {
                    setToogler(prev => !prev);
                  }}>
                  <CircleArrowIcon />
                </Box>
              </Box>
            }
            optionsOffset={({ popper }: { popper: ClientRectObject }) => {
              return [popper.width * -0.25, 0];
            }}
            sx={{
              '.MuiTooltip-tooltip': {
                padding: '8px 14px'
              }
            }}
            disableInteractive={false}>
            <Box
              sx={{
                maxHeight: '24px',
                background: '#F9FFAF'
              }}>
              <KeyPasswordIcon
                style={{ margin: '2px 6px 1px 7px', color: 'var(--mui-palette-text-placeholder)' }}
              />
            </Box>
          </CustomTooltip>
        )}
        {!autofill && (
          <>
            {tooltip && <Box sx={{ mr: '10px' }}> {tooltip}</Box>}
            {isEdit ? (
              <IconButton
                sx={{
                  cursor: onClickIcon ? 'pointer' : 'default',
                  padding: 0,
                  color: 'var(--mui-palette-modal-clearInputIcon)',
                  '&:hover': { color: 'var(--mui-palette-modal-clearInputIconHover)' },
                  '&:active': { color: 'var(--mui-palette-modal-clearInputIconActive)' }
                }}
                onClick={onClickIcon}>
                <EditIcon />
              </IconButton>
            ) : (
              !!methods?.watch(name) && (
                <>
                  {isPass && (
                    <IconButton
                      sx={{ padding: 0, marginRight: '4px', marginLeft: '6px' }}
                      aria-label={showPassword ? 'Hide the password' : 'Display the password'}
                      onClick={() => {
                        setShowPassword?.(!showPassword);
                      }}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  )}
                  {!notClearable && !disabled && (
                    <IconButton
                      sx={{
                        padding: '0px',
                        width: '24px',
                        height: '24px',
                        color: 'var(--mui-palette-modal-clearInputIcon)',
                        '&:hover': { color: 'var(--mui-palette-modal-clearInputIconHover)' },
                        '&:active': { color: 'var(--mui-palette-modal-clearInputIconActive)' }
                      }}
                      onClick={resetField}>
                      <CloseIcon height={'100%'} />
                    </IconButton>
                  )}
                </>
              )
            )}
          </>
        )}
        {autofill && (
          <LockedIcon
            style={{
              color: 'var(--mui-palette-status-pendingColor)'
            }}
          />
        )}
      </Box>
      <Box
        style={{
          marginLeft: '16px',
          fontSize: helperText ? 13 : 12,
          color: helperText ? 'var(--mui-palette-text-primary)' : 'var(--mui-palette-error-main)',
          textAlign: 'start',
          fontWeight: helperText ? 600 : 'normal'
        }}>
        {(helperText && <span>{helperText}</span>) || fieldError}
      </Box>
    </Box>
  );
};
